import React, { useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../api";
import Logo from "../components/Logo";
import ProfileMenu from "../components/ProfileMenu";
import {
  Globe, Search, Network, Activity, Route as RouteIcon, Server, ShieldCheck,
  Plug, Radio, Mail, MailCheck, Terminal, ArrowLeft, Play, Loader2, Wrench,
} from "lucide-react";

const enc = encodeURIComponent;

// Each tool: inputs to render + how to build the request.
const TOOLS = [
  { id: "dns", name: "DNS Lookup", icon: Globe, desc: "A / AAAA / MX / NS / TXT / CNAME / SOA / CAA",
    fields: [{ k: "target", label: "Domain or IP", ph: "example.com" }],
    req: (v) => ({ method: "get", url: `/api/debug/dns?target=${enc(v.target)}` }) },
  { id: "whois", name: "WHOIS", icon: Search, desc: "Registration & ownership",
    fields: [{ k: "target", label: "Domain", ph: "example.com" }],
    req: (v) => ({ method: "get", url: `/api/debug/whois?target=${enc(v.target)}` }) },
  { id: "reverse", name: "Reverse DNS", icon: Network, desc: "PTR record for an IP",
    fields: [{ k: "target", label: "IP or host", ph: "8.8.8.8" }],
    req: (v) => ({ method: "get", url: `/api/debug/reverse-dns?target=${enc(v.target)}` }) },
  { id: "ping", name: "Ping", icon: Activity, desc: "Latency & packet loss",
    fields: [{ k: "target", label: "Host", ph: "example.com" }, { k: "count", label: "Count", type: "number", def: "4" }],
    req: (v) => ({ method: "get", url: `/api/debug/ping?target=${enc(v.target)}&count=${v.count || 4}` }) },
  { id: "traceroute", name: "Traceroute", icon: RouteIcon, desc: "Network path (via nmap)",
    fields: [{ k: "target", label: "Host", ph: "example.com" }],
    req: (v) => ({ method: "get", url: `/api/debug/traceroute?target=${enc(v.target)}` }) },
  { id: "http", name: "HTTP Debug", icon: Server, desc: "Status, redirects, timing, headers",
    fields: [{ k: "url", label: "URL", ph: "https://example.com" }],
    req: (v) => ({ method: "get", url: `/api/debug/http?url=${enc(v.url)}` }) },
  { id: "headers", name: "Response Headers", icon: Terminal, desc: "Security headers, cookies, CORS",
    fields: [{ k: "target", label: "Domain", ph: "example.com" }],
    req: (v) => ({ method: "get", url: `/api/debug/headers?target=${enc(v.target)}` }) },
  { id: "ssl", name: "SSL Inspector", icon: ShieldCheck, desc: "Cert, protocols, ciphers, OCSP, chain",
    fields: [{ k: "target", label: "Domain", ph: "example.com" }],
    req: (v) => ({ method: "get", url: `/api/debug/ssl?target=${enc(v.target)}` }) },
  { id: "tcp", name: "TCP Port Check", icon: Plug, desc: "Connect + banner grab",
    fields: [{ k: "target", label: "Host", ph: "example.com" }, { k: "port", label: "Port", type: "number", def: "443" }],
    req: (v) => ({ method: "get", url: `/api/debug/tcp?target=${enc(v.target)}&port=${v.port || 443}` }) },
  { id: "udp", name: "UDP Port Check", icon: Radio, desc: "Best-effort UDP probe",
    fields: [{ k: "target", label: "Host", ph: "example.com" }, { k: "port", label: "Port", type: "number", def: "53" }],
    req: (v) => ({ method: "get", url: `/api/debug/udp?target=${enc(v.target)}&port=${v.port || 53}` }) },
  { id: "smtp", name: "SMTP Check", icon: Mail, desc: "MX, banner, EHLO, STARTTLS",
    fields: [{ k: "target", label: "Domain", ph: "example.com" }],
    req: (v) => ({ method: "get", url: `/api/debug/smtp?target=${enc(v.target)}` }) },
  { id: "email", name: "Email Security", icon: MailCheck, desc: "SPF / DKIM / DMARC / MX",
    fields: [{ k: "target", label: "Domain", ph: "example.com" }],
    req: (v) => ({ method: "get", url: `/api/debug/email-security?target=${enc(v.target)}` }) },
  { id: "api", name: "API Tester", icon: Terminal, desc: "Send any HTTP request",
    fields: [
      { k: "method", label: "Method", type: "select", opts: ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"], def: "GET" },
      { k: "url", label: "URL", ph: "https://api.example.com/v1/ping" },
      { k: "body", label: "Body (optional)", type: "textarea", ph: '{"key":"value"}' },
    ],
    req: (v) => ({ method: "post", url: "/api/debug/api-test", data: { method: v.method || "GET", url: v.url, body: v.body || null } }) },
];

export default function Tools() {
  const [active, setActive] = useState(TOOLS[0]);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const pick = (t) => { setActive(t); setVals({}); setResult(null); };
  const set = (k, v) => setVals((p) => ({ ...p, [k]: v }));

  const run = async () => {
    // Validate the primary INPUT (first field with no default) — not a select
    // like "method" whose default value isn't kept in `vals` until changed.
    const primary = active.fields.find((f) => !f.def) || active.fields[0];
    if (primary && !vals[primary.k]) { toast.error(`Enter ${primary.label.toLowerCase()}`); return; }
    setBusy(true); setResult(null);
    try {
      const r = active.req(vals);
      const res = r.method === "post" ? await api.post(r.url, r.data) : await api.get(r.url);
      setResult(res.data);
      if (res.data?.error) toast.error(res.data.error);
    } catch (e) {
      const msg = e.response?.data?.detail || "Request failed";
      setResult({ error: msg });
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="au-root">
      <nav style={{ padding: "14px 28px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Link to="/" style={{ textDecoration: "none" }}><Logo size="small" /></Link>
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <Link to="/dashboard" style={{ color: "var(--au-text-2)", textDecoration: "none", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}><ArrowLeft size={14} /> Dashboard</Link>
          <ProfileMenu />
        </div>
      </nav>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 24px" }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, color: "var(--au-text)", letterSpacing: 1.5, marginBottom: 6, display: "flex", alignItems: "center", gap: 12 }}>
          <Wrench size={24} style={{ color: "var(--au-accent-ink)" }} /> Debug Toolkit
        </h1>
        <p style={{ color: "var(--au-muted)", marginBottom: 24, fontSize: 14 }}>Network &amp; infrastructure diagnostics — DNS, WHOIS, ping, HTTP, SSL, ports, email security, and more</p>

        <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 18 }}>
          {/* Tool list */}
          <div className="au-glass" style={{ padding: 10, alignSelf: "start", maxHeight: "72vh", overflowY: "auto" }}>
            {TOOLS.map((t) => {
              const Icon = t.icon;
              const on = active.id === t.id;
              return (
                <button key={t.id} onClick={() => pick(t)} type="button"
                  aria-pressed={on}
                  style={{
                    width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 12px", borderRadius: "var(--au-r-input)", marginBottom: 4, cursor: "pointer",
                    background: on ? "var(--au-glass-strong)" : "transparent",
                    border: "1px solid " + (on ? "var(--au-accent)" : "transparent"),
                    color: on ? "var(--au-accent-ink)" : "var(--au-text-2)",
                  }}>
                  <Icon size={16} style={{ flex: "none" }} />
                  <span style={{ fontSize: 13, fontWeight: on ? 700 : 500 }}>{t.name}</span>
                </button>
              );
            })}
          </div>

          {/* Form + result */}
          <div>
            <div className="au-glass au-pad" style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "var(--au-text)", marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
                <active.icon size={16} style={{ color: "var(--au-accent-ink)" }} /> {active.name}
              </div>
              <div style={{ fontSize: 12, color: "var(--au-muted)", marginBottom: 16 }}>{active.desc}</div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
                {active.fields.map((f) => (
                  <div key={f.k} style={{ flex: f.type === "textarea" ? "1 1 100%" : (f.type === "number" || f.type === "select") ? "0 0 120px" : "1 1 240px" }}>
                    <label htmlFor={"f-" + f.k} style={{ display: "block", fontSize: 11, color: "var(--au-muted)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>{f.label}</label>
                    {f.type === "textarea" ? (
                      <textarea id={"f-" + f.k} className="au-input au-mono" rows={4} placeholder={f.ph || ""}
                        value={vals[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} style={{ resize: "vertical" }} />
                    ) : f.type === "select" ? (
                      <select id={"f-" + f.k} className="au-input au-mono" value={vals[f.k] || f.def}
                        onChange={(e) => set(f.k, e.target.value)}>
                        {f.opts.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input id={"f-" + f.k} className="au-input au-mono" type={f.type || "text"} placeholder={f.ph || ""}
                        value={vals[f.k] ?? (f.def || "")} onChange={(e) => set(f.k, e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && !busy && run()} />
                    )}
                  </div>
                ))}
              </div>

              <button onClick={run} disabled={busy} className="au-pill au-pill-primary au-pill-sm" style={{ marginTop: 16 }}>
                {busy ? <><Loader2 size={14} className="au-spin" /> Running…</> : <><Play size={14} /> Run</>}
              </button>
            </div>

            {result && (
              <div className="au-glass au-pad">
                <div className="au-stat-lbl" style={{ letterSpacing: 2, marginBottom: 12 }}>Result</div>
                {result.error ? (
                  <div style={{ color: "var(--au-error)", fontSize: 13, fontFamily: "var(--au-font-mono)" }}>{result.error}</div>
                ) : (
                  <pre style={{ margin: 0, fontFamily: "var(--au-font-mono)", fontSize: 12.5, color: "var(--au-text-2)", lineHeight: 1.7, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "60vh", overflow: "auto" }}>
                    {JSON.stringify(result, null, 2)}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
